<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('qrScanner.title') }}</h1>
      <p>{{ $t('qrScanner.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="scanner-layout">
        <div class="upload-area" @click="triggerUpload" @drop.prevent="handleDrop" @dragover.prevent="dragOver = true" @dragleave="dragOver = false" :class="{ 'drag-over': dragOver }">
          <input ref="fileInput" type="file" accept="image/*" @change="handleFileChange" style="display:none" />
          <div class="upload-content" v-if="!imagePreview">
            <span class="upload-icon">📷</span>
            <p class="upload-hint">{{ $t('qrScanner.uploadHint') }}</p>
            <p class="upload-sub">{{ $t('qrScanner.uploadSub') }}</p>
          </div>
          <div class="image-preview" v-else>
            <img :src="imagePreview" alt="QR Image" />
          </div>
        </div>

        <div class="result-area">
          <div class="result-header">
            <h3>{{ $t('qrScanner.resultLabel') }}</h3>
            <button v-if="result" class="btn btn-small" @click="copyResult">{{ $t('qrScanner.copy') }}</button>
          </div>

          <div class="result-box" v-if="result">
            <div class="result-content">{{ result }}</div>
            <div class="result-meta" v-if="isURL">
              <a :href="result" target="_blank" rel="noopener noreferrer" class="result-link">
                🔗 {{ $t('qrScanner.openLink') }}
              </a>
            </div>
          </div>

          <div class="empty-result" v-else-if="scanning">
            <span class="scanning-icon">⏳</span>
            <p>{{ $t('qrScanner.scanning') }}</p>
          </div>

          <div class="empty-result" v-else-if="errorMsg">
            <span class="error-icon">⚠️</span>
            <p>{{ errorMsg }}</p>
          </div>

          <div class="empty-result" v-else>
            <span class="empty-icon">📋</span>
            <p>{{ $t('qrScanner.emptyResult') }}</p>
          </div>

          <div class="success-msg" v-if="successMsg">{{ successMsg }}</div>

          <div class="action-buttons" v-if="imagePreview">
            <button class="btn btn-secondary" @click="reset">{{ $t('qrScanner.reset') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 使用 paste 事件监听粘贴 -->
    <div class="tool-card paste-hint">
      <p>💡 {{ $t('qrScanner.pasteHint') }}</p>
    </div>

    <div class="tool-card info-card">
      <h2>{{ $t('qrScanner.whatIs') }}</h2>
      <p>{{ $t('qrScanner.whatIsDesc') }}</p>
      <h3>{{ $t('qrScanner.tipsTitle') }}</h3>
      <ul>
        <li v-for="(tip, i) in $t('qrScanner.tips')" :key="i">{{ tip }}</li>
      </ul>
    </div>

    <canvas ref="scanCanvas" style="display:none"></canvas>
  </div>
</template>

<script>
export default {
  name: 'QrScanner',
  data() {
    return {
      imagePreview: null,
      result: '',
      scanning: false,
      errorMsg: '',
      successMsg: '',
      dragOver: false,
      jsQRLoaded: false
    }
  },
  computed: {
    isURL() {
      if (!this.result) return false
      try {
        const url = new URL(this.result)
        return url.protocol === 'http:' || url.protocol === 'https:'
      } catch {
        return false
      }
    }
  },
  methods: {
    triggerUpload() {
      this.$refs.fileInput.click()
    },
    handleFileChange(e) {
      const file = e.target.files[0]
      if (file) this.processFile(file)
    },
    handleDrop(e) {
      this.dragOver = false
      const file = e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.processFile(file)
      }
    },
    handlePaste(e) {
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile()
          if (file) this.processFile(file)
          break
        }
      }
    },
    processFile(file) {
      this.result = ''
      this.errorMsg = ''
      this.scanning = true

      const reader = new FileReader()
      reader.onload = (e) => {
        this.imagePreview = e.target.result
        this.decodeImage(e.target.result)
      }
      reader.readAsDataURL(file)
    },
    decodeImage(dataUrl) {
      const img = new Image()
      img.onload = () => {
        const canvas = this.$refs.scanCanvas
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0)
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)

        if (window.jsQR) {
          const code = window.jsQR(imageData.data, imageData.width, imageData.height)
          this.scanning = false
          if (code) {
            this.result = code.data
          } else {
            this.errorMsg = this.$t('qrScanner.noQrFound')
          }
        } else {
          this.scanning = false
          this.errorMsg = this.$t('qrScanner.libLoading')
        }
      }
      img.src = dataUrl
    },
    async copyResult() {
      try {
        await navigator.clipboard.writeText(this.result)
        this.successMsg = this.$t('qrScanner.copySuccess')
        setTimeout(() => { this.successMsg = '' }, 2000)
      } catch {
        this.successMsg = this.$t('qrScanner.copyFail')
        setTimeout(() => { this.successMsg = '' }, 2000)
      }
    },
    reset() {
      this.imagePreview = null
      this.result = ''
      this.errorMsg = ''
      this.scanning = false
      this.$refs.fileInput.value = ''
    }
  },
  mounted() {
    // 加载 jsQR 库
    if (!window.jsQR) {
      const script = document.createElement('script')
      script.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js'
      script.onload = () => { this.jsQRLoaded = true }
      document.head.appendChild(script)
    } else {
      this.jsQRLoaded = true
    }
    // 监听粘贴事件
    document.addEventListener('paste', this.handlePaste)
  },
  beforeUnmount() {
    document.removeEventListener('paste', this.handlePaste)
  }
}
</script>

<style scoped>
.tool-page { max-width: 900px; margin: 0 auto; }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 28px; color: var(--text-primary); margin-bottom: 8px; }
.tool-header p { color: var(--text-secondary); font-size: 16px; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; margin-bottom: 24px; box-shadow: var(--shadow-sm); }

.scanner-layout { display: flex; gap: 32px; }

.upload-area { flex: 1; min-height: 280px; border: 2px dashed var(--border-color); border-radius: 12px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.3s; overflow: hidden; }
.upload-area:hover, .upload-area.drag-over { border-color: var(--accent-color); background: var(--accent-light); }

.upload-content { text-align: center; padding: 24px; }
.upload-icon { font-size: 56px; display: block; margin-bottom: 12px; }
.upload-hint { font-size: 16px; color: var(--text-primary); font-weight: 500; margin-bottom: 4px; }
.upload-sub { font-size: 13px; color: var(--text-tertiary); }

.image-preview { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; padding: 8px; }
.image-preview img { max-width: 100%; max-height: 320px; border-radius: 8px; object-fit: contain; }

.result-area { flex: 1; display: flex; flex-direction: column; gap: 12px; }
.result-header { display: flex; align-items: center; justify-content: space-between; }
.result-header h3 { font-size: 16px; color: var(--text-primary); margin: 0; }

.result-box { background: var(--bg-tertiary); border-radius: 10px; padding: 16px; }
.result-content { font-size: 15px; color: var(--text-primary); line-height: 1.8; word-break: break-all; white-space: pre-wrap; }
.result-meta { margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border-color); }
.result-link { color: var(--accent-color); text-decoration: none; font-size: 14px; }
.result-link:hover { text-decoration: underline; }

.empty-result { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; color: var(--text-tertiary); gap: 4px; min-height: 120px; }
.empty-icon, .scanning-icon, .error-icon { font-size: 40px; }
.empty-result p { font-size: 14px; margin: 0; }

.btn { padding: 8px 16px; border: none; border-radius: 8px; font-size: 14px; cursor: pointer; transition: all 0.2s; }
.btn-small { padding: 4px 12px; font-size: 13px; background: var(--accent-color); color: white; border-radius: 6px; }
.btn-small:hover { opacity: 0.9; }
.btn-secondary { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); }
.btn-secondary:hover { border-color: var(--accent-color); }

.action-buttons { display: flex; gap: 8px; }
.success-msg { font-size: 14px; color: var(--success-color, #22c55e); }

.paste-hint { padding: 16px 24px; text-align: center; }
.paste-hint p { margin: 0; font-size: 14px; color: var(--text-secondary); }

.info-card h2 { font-size: 20px; color: var(--text-primary); margin-bottom: 12px; }
.info-card h3 { font-size: 16px; color: var(--text-primary); margin: 16px 0 8px; }
.info-card p { font-size: 14px; color: var(--text-secondary); line-height: 1.8; }
.info-card ul { padding-left: 20px; margin: 0; }
.info-card li { font-size: 14px; color: var(--text-secondary); line-height: 2; }

@media (max-width: 768px) {
  .scanner-layout { flex-direction: column; }
  .upload-area { min-height: 200px; }
}
</style>
